import { Injectable, inject } from '@angular/core';
import {
  Firestore, collection, query, orderBy, limit, getDocs,
  addDoc, serverTimestamp,
} from '@angular/fire/firestore';

/** Texte multilingue (miroir de MarketingCampaign côté backoffice). */
export interface BannerText {
  fr?: string;
  nl?: string;
  en?: string;
}

export type BannerPlacement = 'homepage' | 'checkout' | 'tracking' | 'vm_dashboard';

/**
 * Projection publique d'une bannière (collection `public_banners`, maintenue
 * par le trigger syncPublicBanner). Ne contient QUE les champs d'affichage.
 */
export interface PublicBanner {
  id: string;
  title?: BannerText;
  subtitle?: BannerText;
  body?: BannerText;
  bannerImageUrl?: string;
  ctaLabel?: BannerText;
  ctaLink?: string;
  placements?: BannerPlacement[];
  displayOrder?: number;
  publishAt?: any;
  expiresAt?: any;
}

/**
 * #568 — Service frontoffice des bannières marketing.
 *
 * Lit la projection publique `public_banners` (jamais `marketing_campaigns`,
 * fermé au staff) et écrit les événements d'impression/clic dans
 * `campaign_events` (create-only, agrégé en compteurs par un job planifié).
 * Aucun `updateDoc` sur la campagne côté client (refusé par les rules + coût).
 */
@Injectable({ providedIn: 'root' })
export class BannerService {
  private firestore = inject(Firestore);

  /**
   * Bannières actives pour un emplacement donné, triées par `displayOrder`.
   * `public_banners` est une petite collection (campagnes actives uniquement) :
   * on filtre placement + fenêtre de dates côté client pour éviter un index
   * composite (array-contains + orderBy).
   */
  async getActiveBanners(placement: BannerPlacement): Promise<PublicBanner[]> {
    const ref = collection(this.firestore, 'public_banners');
    const q = query(ref, orderBy('displayOrder', 'asc'), limit(20));
    const snap = await getDocs(q);
    const now = new Date();

    return snap.docs
      .map(d => ({ id: d.id, ...d.data() } as PublicBanner))
      .filter(b => {
        if (!b.placements?.includes(placement)) return false;
        const pub = toDate(b.publishAt);
        if (pub && pub > now) return false;
        const exp = toDate(b.expiresAt);
        if (exp && exp < now) return false;
        return true;
      });
  }

  async trackImpression(campaignId: string, placement: BannerPlacement): Promise<void> {
    await this.track(campaignId, 'impression', placement);
  }

  async trackClick(campaignId: string, placement: BannerPlacement): Promise<void> {
    await this.track(campaignId, 'click', placement);
  }

  private async track(
    campaignId: string,
    type: 'impression' | 'click',
    placement: BannerPlacement,
  ): Promise<void> {
    try {
      const ref = collection(this.firestore, 'campaign_events');
      await addDoc(ref, { campaignId, type, placement, createdAt: serverTimestamp() });
    } catch (e) {
      // Best-effort : un événement perdu ne doit jamais casser l'affichage.
      console.warn('[Banner] track failed:', type, e);
    }
  }
}

function toDate(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') return v.toDate();          // Firestore Timestamp
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}
